// src/components/Salary/SalaryEntry.jsx
//
// Salary -> Salary.
// * One Salary Entry per calendar month PER COMPANY. Selecting a company and
//   month loads whatever was saved for that pair (or a blank form for a new
//   one); saving always updates that same (month, company) row - never a
//   duplicate (server: salary_entries has a UNIQUE key on
//   (salary_month, company_id), see migrations/047_salary_entries_company.sql).
//   The server also requires and validates company_id on every read/write, so
//   one company's records can never leak into another's.
// * Total Debit must equal Total Credit before Save is enabled. The same rule
//   is enforced again on the server, so an unbalanced entry can never be saved.
// * Staff Loan allocations select the exact employee loans being repaid. The
//   server revalidates and posts those repayments atomically with this
//   company-level journal; payroll repayments never create Cash Book entries.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faSpinner,
  faCheck,
  faTriangleExclamation,
  faPlus,
  faTimes,
  faChevronDown,
  faChevronRight,
  faTrash,
  faBuilding,
  faCalendarDays,
  faScaleBalanced,
  faReceipt,
  faHandHoldingDollar
} from '@fortawesome/free-solid-svg-icons';
import api from '../../api';
import { usePopup } from '../context/PopupContext';
import '../Finance/FinanceForm.css';
import '../Finance/StaffLoans.css';
import './SalaryEntry.css';

const WRITE_ROLES = ['Admin', 'Super Admin', 'Accountant'];

const getStoredUserRole = () => {
  try {
    const storedUser = localStorage.getItem('user');
    const role = storedUser ? JSON.parse(storedUser).role : null;
    return role === 'QB' ? 'View Only' : role;
  } catch (e) {
    return null;
  }
};

// Mirrors server/utils/salaryEntryUtils.js SALARY_ENTRY_ACCOUNTS. `route` links
// the account's label to its page under the Salary sub-navigation.
const ACCOUNTS = [
  { key: 'salary_expenses', label: 'Salary Expenses', side: 'debit' },
  { key: 'epf_expenses', label: 'EPF Expenses - 12%', side: 'debit' },
  { key: 'etf_expenses', label: 'ETF Expenses - 3%', side: 'debit' },
  { key: 'epf_payable', label: 'EPF Payable - 12%+8% = 20%', side: 'credit', route: '/salary/epf-etf-payable' },
  { key: 'etf_payable', label: 'ETF Payable - 3%', side: 'credit', route: '/salary/epf-etf-payable' },
  { key: 'salary_payable', label: 'Salary Payable', side: 'credit', route: '/salary/salary-payable' },
  { key: 'salary_advance', label: 'Salary Advance', side: 'credit', route: '/salary/salary-advance' },
  { key: 'staff_loan', label: 'Staff Loan', side: 'credit', route: '/salary/staff-loan' },
  { key: 'salary_deduction', label: 'Salary Deduction', side: 'credit', route: '/salary/salary-deduction' }
];

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const todayLocal = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const fmtDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : (iso || '—');
};

const fmtMoney = (value) =>
  (Number.parseFloat(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const fmtMonth = (value) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value || ''));
  if (!match) return value || '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const emptyForm = () => ACCOUNTS.reduce((acc, a) => ({ ...acc, [a.key]: '' }), {});

// Parses a form field to a plain number (blank/invalid -> 0), matching the
// server's "blank account = 0" rule.
const toNumber = (value) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

// Compares as integer cents so float rounding never produces a false mismatch.
const toCents = (value) => Math.round(toNumber(value) * 100);

const groupLoanAllocations = (allocations) => {
  const grouped = new Map();
  (Array.isArray(allocations) ? allocations : []).forEach((allocation) => {
    const employeeId = String(allocation.employee_id);
    if (!grouped.has(employeeId)) {
      grouped.set(employeeId, {
        key: `employee-${employeeId}`,
        employee_id: employeeId,
        employee_code: allocation.employee_code || '',
        employee_name: allocation.employee_name || '',
        department_name: allocation.department_name || '',
        loans: []
      });
    }
    grouped.get(employeeId).loans.push({
      id: String(allocation.staff_loan_id),
      reference: `Staff Loan #${allocation.staff_loan_id}`,
      description: allocation.description || '',
      issued_date: allocation.issued_date,
      loan_amount: Number(allocation.loan_amount) || 0,
      balance: Number(allocation.available_balance) || 0,
      selected: true,
      deduction_amount: String(allocation.deduction_amount ?? '')
    });
  });
  return [...grouped.values()];
};

const flattenLoanAllocations = (groups) =>
  groups.flatMap((group) => group.loans
    .filter((loan) => loan.selected)
    .map((loan) => ({
      employee_id: Number(group.employee_id),
      staff_loan_id: Number(loan.id),
      deduction_amount: loan.deduction_amount
    })));

const getAllocationTotalCents = (groups) =>
  groups.reduce((sum, group) => sum + group.loans.reduce(
    (employeeSum, loan) => employeeSum + (loan.selected ? toCents(loan.deduction_amount) : 0),
    0
  ), 0);

const SalaryEntry = () => {
  const { showPopup } = usePopup();
  const canWrite = WRITE_ROLES.includes(getStoredUserRole());

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companyId, setCompanyId] = useState('');

  const [month, setMonth] = useState(currentMonth());
  const [entryDate, setEntryDate] = useState(todayLocal());
  const [form, setForm] = useState(emptyForm());
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loanAllocations, setLoanAllocations] = useState([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationDraft, setAllocationDraft] = useState([]);
  const [allocationEmployees, setAllocationEmployees] = useState([]);
  const [loadingAllocationEmployees, setLoadingAllocationEmployees] = useState(false);
  const [loadingEmployeeLoans, setLoadingEmployeeLoans] = useState({});
  const [allocationErrors, setAllocationErrors] = useState({});
  const [allocationLoadError, setAllocationLoadError] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [allocationBaseStaffLoanCents, setAllocationBaseStaffLoanCents] = useState(0);

  // Companies already registered in the system (Management -> Companies).
  useEffect(() => {
    let cancelled = false;
    api.get('/companies')
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setCompanies(list);
        setCompanyId((prev) => prev || (list.length > 0 ? String(list[0].id) : ''));
      })
      .catch((error) => {
        if (cancelled) return;
        showPopup('error', error.response?.data?.error || 'Failed to load companies');
      })
      .finally(() => { if (!cancelled) setLoadingCompanies(false); });
    return () => { cancelled = true; };
  }, [showPopup]);

  const fetchHistory = useCallback(async () => {
    if (!companyId) return;
    setLoadingHistory(true);
    try {
      const response = await api.get('/salary-entries', { params: { company_id: companyId } });
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showPopup('error', error.response?.data?.error || 'Failed to load salary entries');
    } finally {
      setLoadingHistory(false);
    }
  }, [companyId, showPopup]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Load whatever is saved for the selected company + month (or a blank form).
  useEffect(() => {
    if (!month || !companyId) return;
    let cancelled = false;
    setLoadingEntry(true);
    api.get(`/salary-entries/${month}`, { params: { company_id: companyId } })
      .then((response) => {
        if (cancelled) return;
        const entry = response.data?.entry;
        if (entry) {
          setForm(ACCOUNTS.reduce((acc, a) => ({ ...acc, [a.key]: String(entry[a.key] ?? 0) }), {}));
          setEntryDate(entry.entry_date || todayLocal());
          setLoanAllocations(Array.isArray(entry.loan_allocations) ? entry.loan_allocations : []);
        } else {
          setForm(emptyForm());
          setEntryDate(todayLocal());
          setLoanAllocations([]);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        showPopup('error', error.response?.data?.error || 'Failed to load salary entry');
        setForm(emptyForm());
        setEntryDate(todayLocal());
        setLoanAllocations([]);
      })
      .finally(() => { if (!cancelled) setLoadingEntry(false); });
    return () => { cancelled = true; };
  }, [month, companyId, showPopup]);

  const handleFieldChange = (key, value) => {
    if (key === 'staff_loan') return;
    // Accept only digits and at most one decimal point with 2 places, matching
    // the server's parseNonNegativeMoney.
    if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const totalDebitCents = ACCOUNTS.filter((a) => a.side === 'debit').reduce((sum, a) => sum + toCents(form[a.key]), 0);
  const totalCreditCents = ACCOUNTS.filter((a) => a.side === 'credit').reduce((sum, a) => sum + toCents(form[a.key]), 0);
  const balanced = totalDebitCents === totalCreditCents;
  const committedLoanTotalCents = loanAllocations.reduce(
    (sum, allocation) => sum + toCents(allocation.deduction_amount),
    0
  );
  const allocationTotalCents = showAllocationModal
    ? getAllocationTotalCents(allocationDraft)
    : committedLoanTotalCents;
  const allocationsMatchStaffLoan = allocationTotalCents === toCents(form.staff_loan);

  useEffect(() => {
    if (!showAllocationModal) return;
    setForm((previous) => ({
      ...previous,
      staff_loan: (getAllocationTotalCents(allocationDraft) / 100).toFixed(2)
    }));
  }, [allocationDraft, showAllocationModal]);

  const validateAllocations = (groups) => {
    const errors = {};
    const seenLoans = new Set();
    const seenEmployees = new Set();
    groups.forEach((group) => {
      if (group.employee_id) {
        const employeeId = String(group.employee_id);
        if (seenEmployees.has(employeeId)) {
          errors[group.key] = 'This employee is already added.';
        }
        seenEmployees.add(employeeId);
      }
      group.loans.forEach((loan) => {
        if (!loan.selected) return;
        const key = `${group.key}:${loan.id}`;
        if (!loan.eligible) {
          errors[key] = 'This loan is no longer eligible for salary deduction.';
          return;
        }
        if (!loan.deduction_amount || !/^\d+(?:\.\d{1,2})?$/.test(loan.deduction_amount) || toCents(loan.deduction_amount) <= 0) {
          errors[key] = 'Enter a deduction greater than zero.';
        } else if (toCents(loan.deduction_amount) > Math.round(Number(loan.balance) * 100)) {
          errors[key] = `Deduction cannot exceed the outstanding balance of Rs. ${fmtMoney(loan.balance)}.`;
        }
        if (seenLoans.has(String(loan.id))) errors[key] = 'This loan is already allocated.';
        seenLoans.add(String(loan.id));
      });
    });
    return errors;
  };
  const currentAllocationErrors = validateAllocations(allocationDraft);

  const openAllocationModal = async () => {
    if (!companyId || !month || !canWrite) return;
    const initialGroups = groupLoanAllocations(loanAllocations);
    setAllocationBaseStaffLoanCents(toCents(form.staff_loan));
    setAllocationDraft(initialGroups);
    setAllocationErrors({});
    setAllocationLoadError('');
    setExpandedEmployees(Object.fromEntries(initialGroups.map((group) => [group.key, true])));
    setShowAllocationModal(true);
    setLoadingAllocationEmployees(true);
    try {
      const params = { company_id: companyId, salary_month: month };
      const response = await api.get('/staff-loans/eligible-employees', { params });
      const employees = Array.isArray(response.data) ? response.data : [];
      setAllocationEmployees(employees);

      const existingLoanGroups = await Promise.all(initialGroups.map(async (group) => {
        const loanResponse = await api.get(`/staff-loans/${group.employee_id}/eligible-loans`, { params });
        const loans = Array.isArray(loanResponse.data) ? loanResponse.data : [];
        const selectedById = new Map(group.loans.map((loan) => [String(loan.id), loan]));
        const updatedLoans = loans.map((loan) => {
          const previous = selectedById.get(String(loan.id));
          return {
            ...loan,
            id: String(loan.id),
            balance: Number(loan.balance) || 0,
            selected: Boolean(previous),
            deduction_amount: previous?.deduction_amount || '',
            eligible: true
          };
        });
        const eligibleIds = new Set(updatedLoans.map((loan) => loan.id));
        group.loans.filter((loan) => !eligibleIds.has(String(loan.id))).forEach((loan) => {
          updatedLoans.push({ ...loan, eligible: false });
        });
        return { ...group, loans: updatedLoans };
      }));
      setAllocationDraft(existingLoanGroups);
    } catch (error) {
      setAllocationLoadError(error.response?.data?.error || 'Failed to load employees and outstanding loans.');
    } finally {
      setLoadingAllocationEmployees(false);
    }
  };

  const addEmployeeAllocation = () => {
    setAllocationDraft((previous) => [
      ...previous,
      { key: `new-${Date.now()}-${previous.length}`, employee_id: '', employee_code: '', employee_name: '', department_name: '', loans: [] }
    ]);
  };

  const selectAllocationEmployee = async (groupKey, employeeId) => {
    const employee = allocationEmployees.find((item) => String(item.id) === String(employeeId));
    if (!employee) {
      setAllocationDraft((previous) => previous.map((group) => (
        group.key === groupKey ? { ...group, employee_id: '', loans: [] } : group
      )));
      return;
    }
    if (allocationDraft.some((group) => group.key !== groupKey && String(group.employee_id) === String(employeeId))) {
      setAllocationErrors((previous) => ({ ...previous, [groupKey]: 'This employee is already added.' }));
      return;
    }
    setAllocationErrors((previous) => {
      const next = { ...previous };
      delete next[groupKey];
      return next;
    });
    setLoadingEmployeeLoans((previous) => ({ ...previous, [groupKey]: true }));
    try {
      const response = await api.get(`/staff-loans/${employeeId}/eligible-loans`, {
        params: { company_id: companyId, salary_month: month }
      });
      const loans = (Array.isArray(response.data) ? response.data : []).map((loan) => ({
        ...loan,
        id: String(loan.id),
        balance: Number(loan.balance) || 0,
        selected: false,
        deduction_amount: '',
        eligible: true
      }));
      setAllocationDraft((previous) => previous.map((group) => (
        group.key === groupKey
          ? {
            ...group,
            employee_id: String(employee.id),
            employee_code: employee.employee_code || '',
            employee_name: employee.employee_name || '',
            department_name: employee.department_name || '',
            loans
          }
          : group
      )));
      setExpandedEmployees((previous) => ({ ...previous, [groupKey]: true }));
    } catch (error) {
      setAllocationErrors((previous) => ({
        ...previous,
        [groupKey]:         error.response?.data?.error || 'Failed to load this employee\'s outstanding loans.'
      }));
    } finally {
      setLoadingEmployeeLoans((previous) => ({ ...previous, [groupKey]: false }));
    }
  };

  const updateAllocationLoan = (groupKey, loanId, changes) => {
    setAllocationDraft((previous) => previous.map((group) => (
      group.key === groupKey
        ? { ...group, loans: group.loans.map((loan) => (loan.id === loanId ? { ...loan, ...changes } : loan)) }
        : group
    )));
    setAllocationErrors((previous) => {
      const next = { ...previous };
      delete next[`${groupKey}:${loanId}`];
      return next;
    });
  };

  const closeAllocationModal = (save) => {
    if (save) {
      const errors = validateAllocations(allocationDraft);
      setAllocationErrors(errors);
      if (
        Object.keys(errors).length > 0
        || Object.keys(allocationErrors).length > 0
        || allocationLoadError
      ) return;
      const nextAllocations = flattenLoanAllocations(allocationDraft);
      setLoanAllocations(nextAllocations.map((allocation) => {
        const group = allocationDraft.find((item) => Number(item.employee_id) === allocation.employee_id);
        const loan = group?.loans.find((item) => Number(item.id) === allocation.staff_loan_id);
        return {
          ...allocation,
          employee_code: group?.employee_code,
          employee_name: group?.employee_name,
          department_name: group?.department_name,
          loan_amount: loan?.loan_amount,
          available_balance: loan?.balance,
          issued_date: loan?.issued_date,
          description: loan?.description
        };
      }));
      setForm((previous) => ({
        ...previous,
        staff_loan: (getAllocationTotalCents(allocationDraft) / 100).toFixed(2)
      }));
      setShowAllocationModal(false);
      return;
    }

    const signature = (items) => JSON.stringify(items
      .map((item) => `${item.employee_id}:${item.staff_loan_id}:${item.deduction_amount}`)
      .sort());
    const currentDraft = signature(flattenLoanAllocations(allocationDraft));
    const originalDraft = signature(loanAllocations);
    if (currentDraft !== originalDraft && !window.confirm('Discard unsaved staff loan allocation changes?')) return;
    setForm((previous) => ({
      ...previous,
      staff_loan: (allocationBaseStaffLoanCents / 100).toFixed(2)
    }));
    setShowAllocationModal(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (saving || !balanced || !companyId || !entryDate || !allocationsMatchStaffLoan) return;

    setSaving(true);
    try {
      const payload = { salary_month: month, entry_date: entryDate, company_id: Number(companyId) };
      ACCOUNTS.forEach((a) => { payload[a.key] = form[a.key] || '0'; });
      payload.loan_allocations = loanAllocations.map((allocation) => ({
        employee_id: Number(allocation.employee_id),
        staff_loan_id: Number(allocation.staff_loan_id),
        deduction_amount: String(allocation.deduction_amount)
      }));
      await api.post('/salary-entries', payload);
      const companyName = companies.find((c) => String(c.id) === String(companyId))?.name || '';
      showPopup('success', `Salary entry for ${companyName} - ${fmtMonth(month)} saved successfully`);
      await fetchHistory();
    } catch (error) {
      showPopup('error', error.response?.data?.error || 'Failed to save salary entry');
    } finally {
      setSaving(false);
    }
  };

  const debitAccounts = ACCOUNTS.filter((a) => a.side === 'debit');
  const creditAccounts = ACCOUNTS.filter((a) => a.side === 'credit');

  const renderAccountRow = (account) => (
    <tr key={account.key} className="se-row">
      <td>
        <div className="se-row-label">
          {account.route ? (
            <Link to={account.route} className="se-account-link" title={`Open ${account.label}`}>
              {account.label}
            </Link>
          ) : <span>{account.label}</span>}
          {account.key === 'staff_loan' && canWrite && (
            <button
              type="button"
              className="btn btn-secondary btn-sm se-allocate-button"
              onClick={openAllocationModal}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faPlus} /> Allocate Loans
            </button>
          )}
        </div>
      </td>
      <td className="sl-num">
        {account.side === 'debit' && (
          <div className="se-amount-field">
            <span className="se-currency">Rs.</span>
            <input
              type="text"
              inputMode="decimal"
              onChange={(e) => handleFieldChange(account.key, e.target.value)}
              placeholder="0.00"
              className="form-input se-amount-input"
              value={form[account.key]}
              disabled={!canWrite}
            />
          </div>
        )}
      </td>
      <td className="sl-num">
        {account.side === 'credit' && (
          <div className="se-amount-field">
            <span className="se-currency">Rs.</span>
            <input
              type="text"
              inputMode="decimal"
              value={account.key === 'staff_loan' ? (form[account.key] || '0.00') : form[account.key]}
              onChange={(e) => handleFieldChange(account.key, e.target.value)}
              placeholder="0.00"
              className="form-input se-amount-input"
              disabled={!canWrite || account.key === 'staff_loan'}
              readOnly={account.key === 'staff_loan'}
            />
          </div>
        )}
      </td>
    </tr>
  );

  return (
    <div className="page-container se-page">
      <header className="page-header se-header">
        <div className="se-header-title">
          <span className="se-header-icon">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </span>
          <div>
            <h2 style={{ margin: 0 }}>Salary Entry</h2>
            <p className="se-header-subtitle">Monthly payroll journal — expenses, payables and deductions in one balanced entry.</p>
          </div>
        </div>
      </header>

      <div className="card form-container se-filter-card">
        <div className="se-filter-grid">
          <div className="form-group se-filter-group">
            <label htmlFor="se-company"><FontAwesomeIcon icon={faBuilding} className="se-label-icon" /> Company <span className="required">*</span></label>
            <select
              id="se-company"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="form-select"
              disabled={loadingCompanies}
            >
              {companies.length === 0 && <option value="">{loadingCompanies ? 'Loading...' : 'No companies found'}</option>}
              {companies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group se-filter-group">
            <label htmlFor="se-month"><FontAwesomeIcon icon={faCalendarDays} className="se-label-icon" /> Salary Month <span className="required">*</span></label>
            <input
              id="se-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group se-filter-group">
            <label htmlFor="se-entry-date"><FontAwesomeIcon icon={faCalendarDays} className="se-label-icon" /> Entry Date <span className="required">*</span></label>
            <input
              id="se-entry-date"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="form-input"
            />
          </div>
        </div>

        {!companyId ? (
          <div className="sl-empty" style={{ marginTop: 'var(--spacing-lg)' }}>
            {loadingCompanies ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</> : 'Select a company to manage its salary entries.'}
          </div>
        ) : loadingEntry ? (
          <div className="sl-empty" style={{ marginTop: 'var(--spacing-lg)' }}>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
          </div>
        ) : (
          <form onSubmit={handleSave} noValidate>
            <div className="sl-table-wrap se-table-wrap">
              <table className="report-table sl-table se-table">
                <thead>
                  <tr>
                    <th>Salary Entry</th>
                    <th className="sl-num">Debit</th>
                    <th className="sl-num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="se-group-header">
                    <td colSpan={3}>
                      <FontAwesomeIcon icon={faReceipt} /> Expenses
                    </td>
                  </tr>
                  {debitAccounts.map(renderAccountRow)}
                  <tr className="se-group-header se-group-header-credit">
                    <td colSpan={3}>
                      <FontAwesomeIcon icon={faHandHoldingDollar} /> Payables &amp; Deductions
                    </td>
                  </tr>
                  {creditAccounts.map(renderAccountRow)}
                </tbody>
                <tfoot>
                  <tr className="se-total-row">
                    <td><FontAwesomeIcon icon={faScaleBalanced} className="se-label-icon" /> <strong>Total</strong></td>
                    <td className="sl-num"><strong>Rs. {fmtMoney(totalDebitCents / 100)}</strong></td>
                    <td className="sl-num"><strong>Rs. {fmtMoney(totalCreditCents / 100)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {canWrite && (
              <>
                {!balanced && (
                  <div className="se-balance-warning" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Total Debit and Total Credit must be equal before saving.
                  </div>
                )}
                {!allocationsMatchStaffLoan && (
                  <div className="se-balance-warning" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Staff Loan must match its loan allocations. Use Allocate Loans to review them.
                  </div>
                )}
                {!entryDate && (
                  <div className="se-balance-warning" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    Entry Date is required before saving.
                  </div>
                )}
                <div className="form-actions se-actions">
                  <button className="btn btn-primary" type="submit" disabled={saving || !balanced || !allocationsMatchStaffLoan || !entryDate}>
                    {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faCheck} /> Save Salary Entry</>}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>

      {showAllocationModal && (
        <div className="sl-modal-overlay se-allocation-overlay" onClick={() => closeAllocationModal(false)}>
          <div
            className="sl-modal sl-modal-wide se-allocation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="se-allocation-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sl-modal-header se-allocation-header">
              <div>
                <h3 id="se-allocation-title">Allocate Staff Loans</h3>
                <p>Select employees and their loans to deduct from this salary entry.</p>
                <div className="se-allocation-context">
                  <span>{companies.find((company) => String(company.id) === String(companyId))?.name || 'Company'}</span>
                  <span>{fmtMonth(month)}</span>
                </div>
              </div>
              <button type="button" className="sl-modal-close" onClick={() => closeAllocationModal(false)} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="sl-modal-body se-allocation-body">
              <div className="se-allocation-summary">
                <div className="se-summary-card">
                  <span>Total Selected Employees</span>
                  <strong>{allocationDraft.filter((group) => group.loans.some((loan) => loan.selected)).length}</strong>
                </div>
                <div className="se-summary-card">
                  <span>Total Loan Deduction</span>
                  <strong>Rs. {fmtMoney(allocationTotalCents / 100)}</strong>
                </div>
                <div className="se-summary-card">
                  <span>Remaining Salary Payable</span>
                  <strong>Rs. {fmtMoney(Math.max(0, toCents(form.salary_payable) - allocationTotalCents) / 100)}</strong>
                </div>
              </div>

              {allocationLoadError && <div className="sl-modal-error" role="alert">{allocationLoadError}</div>}

              {allocationDraft.map((group) => {
                const employeeTotalCents = group.loans.reduce(
                  (sum, loan) => sum + (loan.selected ? toCents(loan.deduction_amount) : 0),
                  0
                );
                const isExpanded = expandedEmployees[group.key] !== false;
                const selectedEmployees = allocationDraft
                  .filter((item) => item.key !== group.key && item.employee_id)
                  .map((item) => String(item.employee_id));
                return (
                  <section className="se-employee-allocation" key={group.key}>
                    <div className="se-employee-allocation-header">
                      <button
                        type="button"
                        className="se-expand-button"
                        onClick={() => setExpandedEmployees((previous) => ({
                          ...previous,
                          [group.key]: !isExpanded
                        }))}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} employee allocation`}
                      >
                        <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                      </button>
                      <div className="se-employee-allocation-title">
                        <strong>{group.employee_name || 'Select employee'}</strong>
                        {group.employee_code && <span>{group.employee_code}</span>}
                        {group.department_name && <span>{group.department_name}</span>}
                      </div>
                      <strong className="se-employee-total">Rs. {fmtMoney(employeeTotalCents / 100)}</strong>
                      <button
                        type="button"
                        className="sl-icon-btn"
                        onClick={() => setAllocationDraft((previous) => previous.filter((item) => item.key !== group.key))}
                        aria-label={`Remove ${group.employee_name || 'employee'} allocation`}
                        title="Remove employee allocation"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="se-employee-allocation-content">
                        <div className="form-group">
                          <label htmlFor={`se-employee-${group.key}`}>Employee</label>
                          <select
                            id={`se-employee-${group.key}`}
                            className="form-select"
                            value={group.employee_id}
                            onChange={(event) => selectAllocationEmployee(group.key, event.target.value)}
                            disabled={loadingAllocationEmployees || loadingEmployeeLoans[group.key]}
                          >
                            <option value="">Select an employee</option>
                            {allocationEmployees
                              .filter((employee) => (
                                !selectedEmployees.includes(String(employee.id))
                                || String(employee.id) === String(group.employee_id)
                              ))
                              .map((employee) => (
                                <option key={employee.id} value={employee.id}>
                                  {employee.employee_name} ({employee.employee_code})
                                </option>
                              ))}
                            {group.employee_id && !allocationEmployees.some((employee) => String(employee.id) === String(group.employee_id)) && (
                              <option value={group.employee_id}>
                                {group.employee_name} ({group.employee_code}) — no longer eligible
                              </option>
                            )}
                          </select>
                          {allocationErrors[group.key] && <span className="form-error">{allocationErrors[group.key]}</span>}
                        </div>

                        {!group.employee_id ? (
                          <div className="sl-empty">Choose an employee to load their outstanding loans.</div>
                        ) : loadingEmployeeLoans[group.key] ? (
                          <div className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading outstanding loans...</div>
                        ) : group.loans.length === 0 ? (
                          <div className="sl-empty">This employee has no eligible outstanding loans for this company and salary month.</div>
                        ) : (
                          <div className="sl-table-wrap">
                            <table className="report-table sl-table se-loan-allocation-table">
                              <thead>
                                <tr>
                                  <th>Select</th>
                                  <th>Loan</th>
                                  <th>Issue Date</th>
                                  <th className="sl-num">Outstanding</th>
                                  <th className="sl-num">Deduction</th>
                                  <th className="sl-num">Remaining</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.loans.map((loan) => {
                                  const deductionCents = loan.selected ? toCents(loan.deduction_amount) : 0;
                                  const loanError = allocationErrors[`${group.key}:${loan.id}`]
                                    || currentAllocationErrors[`${group.key}:${loan.id}`];
                                  return (
                                    <tr key={loan.id} className={!loan.eligible ? 'sl-row-settled' : ''}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          aria-label={`Select ${loan.reference}`}
                                          checked={loan.selected}
                                          disabled={!loan.eligible}
                                          onChange={(event) => updateAllocationLoan(group.key, loan.id, { selected: event.target.checked })}
                                        />
                                      </td>
                                      <td>
                                        <strong>{loan.reference}</strong>
                                        {loan.description && <small className="se-loan-description">{loan.description}</small>}
                                        {!loan.eligible && <small className="form-error">No longer eligible</small>}
                                      </td>
                                      <td>{loan.issued_date || '—'}</td>
                                      <td className="sl-num">Rs. {fmtMoney(loan.balance)}</td>
                                      <td className="sl-num">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          value={loan.deduction_amount}
                                          disabled={!loan.selected}
                                          onChange={(event) => {
                                            if (event.target.value === '' || /^\d*\.?\d{0,2}$/.test(event.target.value)) {
                                              updateAllocationLoan(group.key, loan.id, { deduction_amount: event.target.value });
                                            }
                                          }}
                                          aria-label={`Deduction for ${loan.reference}`}
                                          className="form-input se-loan-deduction-input"
                                        />
                                        {loanError && <span className="form-error">{loanError}</span>}
                                      </td>
                                      <td className="sl-num">
                                        Rs. {fmtMoney(Math.max(0, Math.round(Number(loan.balance) * 100) - deductionCents) / 100)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}

              {allocationDraft.length === 0 && !loadingAllocationEmployees && (
                <div className="sl-empty">No employees added yet. Add an employee to begin allocating loan deductions.</div>
              )}
              {loadingAllocationEmployees && (
                <div className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading eligible employees...</div>
              )}
            </div>

            <div className="sl-modal-footer se-allocation-footer">
              <button type="button" className="btn btn-secondary" onClick={addEmployeeAllocation}>
                <FontAwesomeIcon icon={faPlus} /> Add Employee Loan Allocation
              </button>
              <div>
                <button type="button" className="btn btn-secondary" onClick={() => closeAllocationModal(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => closeAllocationModal(true)}
                  disabled={
                    loadingAllocationEmployees
                    || Object.values(loadingEmployeeLoans).some(Boolean)
                    || Object.keys(currentAllocationErrors).length > 0
                    || Object.keys(allocationErrors).length > 0
                    || Boolean(allocationLoadError)
                  }
                >
                  Save Allocation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card sl-loans-card">
        <h3 className="sl-section-title">Saved Salary Entries</h3>
        {!companyId ? (
          <div className="sl-empty">Select a company to see its saved entries.</div>
        ) : loadingHistory ? (
          <div className="sl-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
        ) : (
          <div className="sl-table-wrap">
            <table className="report-table sl-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Entry Date</th>
                  <th className="sl-num">Total Debit</th>
                  <th className="sl-num">Total Credit</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={5} className="sl-empty">No salary entries found.</td></tr>
                ) : history.map((entry) => (
                  <tr key={entry.id} className={entry.salary_month === month ? 'sl-row-settled' : ''}>
                    <td>{fmtMonth(entry.salary_month)}</td>
                    <td>{fmtDate(entry.entry_date)}</td>
                    <td className="sl-num">{fmtMoney(entry.total_debit)}</td>
                    <td className="sl-num">{fmtMoney(entry.total_credit)}</td>
                    <td>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMonth(entry.salary_month)}>
                        View / Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalaryEntry;
